import { useCallback, useMemo, useState } from 'react';
import {
  foldPlaylistPages,
  queueCurrent,
  queueVideosToSave,
  PLAYLIST_FIRST_PAGE,
  type Playlist,
} from '@visious/core';
import { fetchAuthPlaylistPage } from '../api/playlists';
import { describeAuthProblem, type ProblemCopy } from '../api/problem';
import type { AuthApiProblem } from '../api/auth';
import { getPlatform } from '../platform/instance';
import { useSession } from '../platform/session';
import { describeWriteProblem, ownedPlaylistsProblem } from '../playlists/model';
import { useEntryMutations } from '../playlists/mutations';
import { useOwnedPlaylists } from '../playlists/useOwnedPlaylists';
import { useQueueFlow } from './flowContext';
import {
  playlistMembershipSettled,
  saveRefusedText,
  savedText,
  saveTargetRows,
  type SaveTargetRow,
} from './saveModel';
import { readQueue } from './store';

/**
 * Saving the queue into one of the reader's own playlists — the flow behind the panel's Save control.
 *
 * Three things happen in one press, and the order is the whole of it:
 *
 * - **What does that playlist already hold.** The owned-playlists response embeds up to 100 entries per
 *   playlist, so for a short one the answer arrived with the list and costs nothing (`planQueueSave`).
 *   For a longer one the playlist is read out first, because the endpoint accepts the same video twice
 *   without complaint (§7i) and a guess would leave a duplicate rather than a refusal.
 * - **One request per video, in the queue's own order.** The playlist has no bulk add, so the list is
 *   sent row by row, and a refusal stops the run where it is: the sentence afterwards says what did
 *   land, since a reader told "saved" about half a list has been told something false.
 * - **The link comes last and goes through the address**, which the caller does
 *   (`useWatchScreen`'s `listKeep.linkTo`). The queue then becomes that playlist, projected in by the
 *   panel's ordinary loader, so the rows are the playlist's own in the playlist's own order.
 *
 * The outcome is spoken on the queue's one `role="status"` line (`queue/QueueActionsHost.tsx`), the same
 * place every other press on this list reports to. A **refusal** is also shown in the dialog, because
 * that is the one outcome where nothing else on screen changes.
 */
export interface SaveQueueFlow {
  readonly signedIn: boolean;
  /** The reader's own playlists as rows. `null` while the list has not arrived. */
  readonly rows: readonly SaveTargetRow[] | null;
  readonly loading: boolean;
  /** The list itself did not load. */
  readonly loadProblem: ProblemCopy | null;
  /** A refused save, already a whole sentence — what was refused, and what had landed before it. */
  readonly problem: string | null;
  /** A read or a run of adds is in flight, which is what makes every row decline a second press. */
  readonly working: boolean;
  readonly save: (playlistId: string, playlistTitle: string) => void;
}

/** A playlist that has just been created holds nothing, so there is nothing to read before sending. */
const NOTHING_HELD: ReadonlySet<string> = new Set<string>();

export function useSaveQueue(input: {
  /** The dialog is open, which is when the expensive list may be fetched and never before. */
  readonly open: boolean;
  /**
   * The queue is now that playlist's. `index` is where the video that is playing landed, when this save
   * is what put it there — the read's opening guess, so it costs one request instead of a walk.
   */
  readonly onSaved: (playlistId: string, index: number | null) => void;
}): SaveQueueFlow {
  const { open, onSaved } = input;
  const session = useSession();
  const signedIn = session.ready && session.facts.signedIn;
  const owned = useOwnedPlaylists(open && signedIn);
  const entries = useEntryMutations();
  const flow = useQueueFlow();
  const [working, setWorking] = useState<{
    readonly playlistId: string;
    readonly state: 'reading' | 'saving';
  } | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const rows = useMemo(() => saveTargetRows(owned.playlists, working), [owned.playlists, working]);

  const loadProblem = useMemo(
    () => (owned.problem === null ? null : describeAuthProblem(owned.problem, ownedPlaylistsProblem())),
    [owned.problem],
  );

  /**
   * Every video id the playlist holds, read out page by page.
   *
   * The **authenticated** endpoint for every row here, without asking: they all came from
   * `/api/v1/auth/playlists`, so each is an `IVPL…` list this account owns, and the public read answers
   * `404` for a private one. The pages overlap and repeat rows inside themselves (§7g), so the fold is
   * what turns them into a list — its own rule, "the last page added no new `index`", is also what stops
   * the walk.
   */
  const readHeldVideoIds = useCallback(
    async (
      playlistId: string,
    ): Promise<
      | { readonly ok: true; readonly held: ReadonlySet<string> }
      | { readonly ok: false; readonly problem: AuthApiProblem }
    > => {
      const boot = await getPlatform();
      if (!boot.ok) return { ok: false, problem: { kind: 'bad-url', path: '/api/v1/auth/playlists' } };

      const pages: Playlist[] = [];
      let page: number | null = PLAYLIST_FIRST_PAGE;
      while (page !== null) {
        // `null` scopes on a read: the instance is the authority (`useOwnedPlaylists.ts`).
        const result = await fetchAuthPlaylistPage(boot.platform, playlistId, page, null);
        if (!result.ok) return { ok: false, problem: result.error };
        pages.push(result.value);
        page = foldPlaylistPages(pages).nextPage;
      }
      const held = new Set(foldPlaylistPages(pages).entries.map((entry) => entry.video.videoId));
      return { ok: true, held };
    },
    [],
  );

  const save = useCallback(
    (playlistId: string, playlistTitle: string) => {
      if (working !== null) return;
      const target = owned.playlists?.find((playlist) => playlist.playlistId === playlistId) ?? null;
      void (async () => {
        setProblem(null);
        /*
         * What that playlist holds. A playlist created from this dialog is not in the cached list yet and
         * holds nothing; a short one arrived with its whole contents embedded; a long one is read out.
         */
        let held = NOTHING_HELD;
        if (target !== null) {
          if (!playlistMembershipSettled(readQueue(), target)) {
            setWorking({ playlistId, state: 'reading' });
            const read = await readHeldVideoIds(playlistId);
            if (!read.ok) {
              const sentence = saveRefusedText(
                describeWriteProblem(read.problem, 'add').title,
                0,
                playlistTitle,
              );
              setProblem(sentence);
              flow?.report(sentence);
              setWorking(null);
              return;
            }
            held = read.held;
          } else {
            held = new Set(target.entries.map((entry) => entry.video.videoId));
          }
        }

        setWorking({ playlistId, state: 'saving' });
        /*
         * One snapshot for both numbers, taken after the read rather than before it: a video may have
         * ended and moved the list on while a long playlist was being walked, and "how many went across"
         * and "how many were already there" have to be counted off the same list to add up.
         *
         * `distinct` is every video the queue could send — its distinct ones, minus the rows a playlist
         * blanked — so what is left after `send` is what the playlist already held.
         */
        const state = readQueue();
        const distinct = queueVideosToSave(state, NOTHING_HELD).length;
        const send = queueVideosToSave(state, held);
        /* Where the video that is playing lands, for the route the caller then goes to. */
        const playingVideoId = queueCurrent(state)?.videoId ?? null;
        let playingIndex: number | null = null;
        let added = 0;

        for (const videoId of send) {
          const result = await entries.add(playlistId, videoId);
          if (!result.ok) {
            const sentence = saveRefusedText(
              describeWriteProblem(result.problem, 'add').title,
              added,
              playlistTitle,
            );
            setProblem(sentence);
            flow?.report(sentence);
            setWorking(null);
            // No link: a playlist holding part of the list is not the list, and pressing again sends
            // only what is still missing.
            return;
          }
          added += 1;
          // §7i answers an add with the whole entry row, so the position comes back with it.
          if (result.value.video.videoId === playingVideoId) playingIndex = result.value.index;
        }

        flow?.report(savedText(added, Math.max(0, distinct - send.length), playlistTitle));
        setWorking(null);
        onSaved(playlistId, playingIndex);
      })();
    },
    [entries, flow, onSaved, owned.playlists, readHeldVideoIds, working],
  );

  return {
    signedIn,
    rows,
    loading: owned.loading,
    loadProblem,
    problem,
    working: working !== null,
    save,
  };
}
